import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  FiHome as DashboardIcon,
  FiUsers as EmployeesIcon,
  FiFileText as LogsIcon,
  FiPlusCircle as RegisterIcon,
  FiMessageSquare as RequestsIcon,
  FiSettings as ConfigIcon
} from 'react-icons/fi';
import { apiFetch } from '../api';

import logo from '../assets/logo.png';
import { getCompanyLabels } from '../utils/companyRoles';

const Sidebar = () => {
  const [currentUser, setCurrentUser] = useState(() => JSON.parse(localStorage.getItem('user') || '{}'));
  const [labels, setLabels] = useState(() => getCompanyLabels(currentUser?.companyName));
  const [entityLabelPlural, setEntityLabelPlural] = useState(() => labels.employeePlural);

  useEffect(() => {
    const handleAuthChange = () => {
      const newUser = JSON.parse(localStorage.getItem('user') || '{}');
      setCurrentUser(newUser);
      const newLabels = getCompanyLabels(newUser?.companyName);
      setLabels(newLabels);
      setEntityLabelPlural(newLabels.employeePlural);
    };

    window.addEventListener('storage', handleAuthChange);
    window.addEventListener('auth-change', handleAuthChange);
    
    return () => {
      window.removeEventListener('storage', handleAuthChange);
      window.removeEventListener('auth-change', handleAuthChange);
    };
  }, []);

  useEffect(() => {
    const fetchSiteConfig = async () => {
      if (!currentUser?.role || currentUser.role.toLowerCase() === 'superadmin') {
        return;
      }
      try {
        const response = await apiFetch('/api/site-config');
        const data = await response.json();
        if (response.ok && data.data?.entityLabelPlural) {
          setEntityLabelPlural(data.data.entityLabelPlural);
        }
      } catch (err) {
        // fallback to default
      }
    };
    fetchSiteConfig();

    window.addEventListener('siteConfigUpdated', fetchSiteConfig);
    return () => {
      window.removeEventListener('siteConfigUpdated', fetchSiteConfig);
    };
  }, [currentUser]);

  const navItems = [
    { name: 'Companies', path: '/companies', icon: <DashboardIcon size={20} />, roles: ['superadmin'] },
    { name: 'Default Forms', path: '/configuration', icon: <ConfigIcon size={20} />, roles: ['superadmin'] },
    { name: 'Dashboard', path: '/dashboard', icon: <DashboardIcon size={20} />, roles: ['admin', 'receptionist', 'employee'] },
    { name: entityLabelPlural, path: '/employees', icon: <EmployeesIcon size={20} />, roles: ['admin'] },
    { name: 'Visit Requests', path: '/visit-requests', icon: <RequestsIcon size={20} />, roles: ['admin', 'receptionist', 'employee'] },
    { name: `Register ${labels.visitor}`, path: '/register', icon: <RegisterIcon size={20} />, roles: ['admin', 'receptionist', 'employee'] },
    { name: `${labels.visitor} Activity`, path: '/logs', icon: <LogsIcon size={20} />, roles: ['admin', 'receptionist', 'employee'] },
  ];

  // Default a missing role to the least-privileged 'employee', never 'admin' —
  // a role-less user must not be shown admin-only items (e.g. Employees).
  const filteredNavItems = navItems.filter(item =>
    item.roles.includes(currentUser.role?.toLowerCase() || 'employee')
  );

  return (
    <aside className="vms-sidebar fixed inset-y-0 left-0 z-50 flex flex-col text-white transition-all duration-300">
      <div className="vms-sidebar-logo">
        <div className="vms-brand" aria-label="VMS Visitor Management System">
          <img src={logo} alt="VMS Logo" className="w-[58px] h-[58px] rounded-[18px] object-cover shadow-[0_16px_30px_rgba(0,42,72,0.28)]" />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar flex flex-col items-center">
        {filteredNavItems.map((item) => (
          <NavLink
            key={item.name}
            to={item.path}
            className={({ isActive }) =>
              `vms-sidebar-link group ${isActive
                ? 'is-active'
                : ''
              }`
            }
          >
            <div className="vms-sidebar-icon">
              {item.icon}
            </div>
            <span className="vms-sidebar-label">
              {item.name}
            </span>
          </NavLink>
        ))}
      </div>

    </aside>
  );
};

export default Sidebar;

